import { TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { IOpportunity } from '@techspert-io/opportunities';
import { debounceTime, startWith } from 'rxjs';
import { MaterialModule } from '../../../../shared/patterns/material-module';
import { IOpportunitySegmentData } from '../../../opportunity-segments/models/opportunity-segments.models';
import { EnrichedUser } from '../../../users/models/user.models';
import {
  HonorariaOperator,
  IMvpExpertFilter,
  IMvpExpertJobHistory,
  IMvpExpertViewEnriched,
  MvpExpertProgression,
  MvpExpertType,
} from '../../models/mvp-experts.models';
import {
  MvpExpertTypePipe,
  MvpExpertTypeTooltipPipe,
} from '../../pipes/mvp-expert-type.pipe';

@Component({
  selector: 'app-mvp-experts-filter',
  templateUrl: './mvp-experts-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MaterialModule,
    TitleCasePipe,
    MvpExpertTypePipe,
    MvpExpertTypeTooltipPipe,
  ],
})
export class MvpExpertsFilterComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly experts = input<IMvpExpertViewEnriched[]>([]);
  readonly internalUsers = input<Record<string, EnrichedUser>>({});
  readonly segments = input<Record<string, IOpportunitySegmentData>>({});
  readonly projectType = input<IOpportunity['projectType']>(null);
  readonly filterChange = output<IMvpExpertFilter>();

  readonly showHonorariaFilter = computed(() => {
    const type = this.projectType();
    return type === 'Qual' || type === 'Quant';
  });

  private readonly resetHonorariaEffect = effect(() => {
    if (!this.showHonorariaFilter()) {
      this.form.patchValue({
        honorariaCurrency: null,
        honorariaAmount: null,
        honorariaOperator: null,
      });
    }
  });

  readonly progressionOptions: MvpExpertProgression[] = [
    'identified',
    'solicitation',
    'responded',
    'screening',
    'client portal',
    'scheduled',
    'complete',
    'rejected',
  ];

  readonly typeOptions: MvpExpertType[] = [
    MvpExpertType.Rolodex,
    MvpExpertType.Sms,
  ];

  readonly operatorOptions: { value: HonorariaOperator; label: string }[] = [
    { value: 'gte', label: '>=' },
    { value: 'lte', label: '<=' },
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'eq', label: '=' },
  ];

  readonly ownerData = computed(() => {
    const counts = this.experts().reduce(
      (map, e) =>
        e.ownerConnectId
          ? map.set(e.ownerConnectId, (map.get(e.ownerConnectId) ?? 0) + 1)
          : map,
      new Map<string, number>()
    );

    const options = [...counts.keys()].map((id) => {
      const user = this.internalUsers()[id];
      const label = user ? `${user.first_name} ${user.last_name}` : id;
      return { id, label };
    });

    return { options, counts };
  });

  readonly segmentData = computed(() => {
    const counts = this.experts().reduce(
      (map, e) =>
        e.opportunitySegmentId
          ? map.set(
              e.opportunitySegmentId,
              (map.get(e.opportunitySegmentId) ?? 0) + 1
            )
          : map,
      new Map<string, number>()
    );

    const options = [...counts.keys()].map((id) => {
      const seg = this.segments()[id];
      const label = seg ? `${seg.geography} · ${seg.segment}` : id;
      return { id, label };
    });

    return { options, counts };
  });

  readonly countryData = computed(() => {
    const counts = this.experts().reduce(
      (map, e) =>
        e.country ? map.set(e.country, (map.get(e.country) ?? 0) + 1) : map,
      new Map<string, number>()
    );

    return { options: [...counts.keys()], counts };
  });

  readonly currencyData = computed(() => {
    const currencies = new Set<string>();
    this.experts().forEach((e) => {
      if (e.honoraria?.currency) currencies.add(e.honoraria.currency);
    });
    return [...currencies];
  });

  readonly form = new FormGroup({
    search: new FormControl('', { nonNullable: true }),
    countries: new FormControl<string[]>([], { nonNullable: true }),
    currentJobOnly: new FormControl(false, { nonNullable: true }),
    companies: new FormControl<string[]>([], { nonNullable: true }),
    industries: new FormControl<string[]>([], { nonNullable: true }),
    jobTitles: new FormControl<string[]>([], { nonNullable: true }),
    types: new FormControl<MvpExpertType[]>([], { nonNullable: true }),
    ownerConnectIds: new FormControl<string[]>([], { nonNullable: true }),
    opportunitySegmentIds: new FormControl<string[]>([], { nonNullable: true }),
    progressions: new FormControl<MvpExpertProgression[]>([], {
      nonNullable: true,
    }),
    honorariaCurrency: new FormControl<string | null>(null),
    honorariaAmount: new FormControl<number | null>(null),
    honorariaOperator: new FormControl<HonorariaOperator | null>(null),
  });

  private readonly currentJobOnlyValue = toSignal(
    this.form.controls.currentJobOnly.valueChanges,
    { initialValue: false }
  );

  readonly companyData = computed(() => this.jobHistoryData('company'));
  readonly jobTitleData = computed(() => this.jobHistoryData('jobTitle'));

  readonly typeData = computed(() => {
    const counts = this.experts().reduce(
      (map, e) =>
        e.mvpTypes.reduce((m, t) => m.set(t, (m.get(t) ?? 0) + 1), map),
      new Map<MvpExpertType, number>()
    );

    return { options: this.typeOptions, counts };
  });

  readonly progressionData = computed(() => {
    const counts = this.experts().reduce(
      (map, e) => map.set(e.progression, (map.get(e.progression) ?? 0) + 1),
      new Map<MvpExpertProgression, number>()
    );

    return { options: this.progressionOptions, counts };
  });

  ngOnInit() {
    this.form.valueChanges
      .pipe(
        startWith(null),
        debounceTime(250),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.filterChange.emit(this.form.getRawValue());
      });
  }

  private jobHistoryData<K extends keyof IMvpExpertJobHistory>(
    key: K
  ): { options: string[]; counts: Map<string, number> } {
    const currentJobOnly = this.currentJobOnlyValue();
    const counts = this.experts().reduce((map, e) => {
      new Set(
        e.jobHistory
          .filter((j) => !currentJobOnly || !j.endDate)
          .map((j) => j[key])
          .filter((v): v is string => !!v)
      ).forEach((val) => map.set(val, (map.get(val) ?? 0) + 1));

      return map;
    }, new Map<string, number>());

    return { options: [...counts.keys()], counts };
  }
}
